import * as React from "react";
import { useContent } from "../hooks/useContent";
import { parseStripeReturn } from "../components/reading/useStripeReturn";
import { parseBookingReturn } from "../components/booking/parseBookingReturn";
import { serviceItem, deckOrderItem, READING_SERVICE_KEY } from "./catalog";
import { track } from "./track";
import { analyticsEnabled } from "./loader";

const CLAIM_PREFIX = "dv-tracked-purchase:";

/** ?deck_paid=1&deck=<ключи через запятую> — success_url магазина
 *  (backend/apps/shop/stripe_checkout.py). Единственный из трёх возвратов, у
 *  которого до сих пор не было парсера: покупку колоды фронт не ловил вовсе. */
export function parseDeckReturn(search: string): { deckKeys: string } | null {
  const params = new URLSearchParams(search);
  if (params.get("deck_paid") !== "1") return null;
  const deckKeys = params.get("deck");
  if (!deckKeys) return null;
  return { deckKeys };
}

/** Помечает покупку как учтённую. true — первый раз, false — повтор.
 *  Дедупликация обязана держаться на одном localStorage: чистить query-параметры
 *  нельзя, они принадлежат BookingPaidModal (экран успеха брони) и ReadingPage
 *  (восстановление оплаченной сессии). */
function claimPurchase(id: string): boolean {
  try {
    const key = CLAIM_PREFIX + id;
    if (window.localStorage.getItem(key)) return false;
    window.localStorage.setItem(key, "1");
    return true;
  } catch {
    // Приватный режим или отключённое хранилище: лучше задвоить событие,
    // чем упасть.
    return true;
  }
}

/** Отправляет purchase при возврате со Stripe. Живёт в App.js — три вида
 *  возврата в одном месте вместо трёх. */
export function useStripePurchase(): void {
  const { data: content } = useContent();

  // Строка запроса снимается при первом рендере, до эффектов детей: у
  // BookingPaidModal есть свой эффект, стирающий booking_paid из URL, и он
  // выполнится раньше, чем догрузится content и сработает эффект ниже.
  const search = React.useRef(
    typeof window === "undefined" ? "" : window.location.search,
  ).current;

  React.useEffect(() => {
    if (!content || !analyticsEnabled()) return;
    // Номера заказа в URL нет ни у колод, ни у броней, поэтому ключ
    // дедупликации — товар плюс дата. От перезагрузки страницы возврата и от
    // пересланной ссылки защищает; та же покупка на следующий день зачтётся снова.
    const day = new Date().toISOString().slice(0, 10);

    const reading = parseStripeReturn(search);
    if (reading) {
      const item = serviceItem(content, READING_SERVICE_KEY);
      if (item && claimPurchase(reading.sessionId)) {
        track({ name: "purchase", orderId: reading.sessionId, ...item });
      }
      return;
    }

    const decks = parseDeckReturn(search);
    if (decks) {
      const item = deckOrderItem(content, decks.deckKeys);
      const orderId = `deck:${decks.deckKeys}:${day}`;
      if (item && claimPurchase(orderId)) {
        track({ name: "purchase", orderId, ...item });
      }
      return;
    }

    const serviceKey = parseBookingReturn(search);
    if (serviceKey) {
      const item = serviceItem(content, serviceKey);
      const orderId = `booking:${serviceKey}:${day}`;
      if (item && claimPurchase(orderId)) {
        track({ name: "purchase", orderId, ...item });
      }
    }
  }, [content, search]);
}
